/**
 * Utility functions for TradingView widget
 */
import { TRADINGVIEW_SCRIPT_URL } from "../../constants";

declare global {
  interface Window {
    TradingView: any;
  }
}

export interface TradingViewWidgetConfig {
  symbol: string;
  interval: string;
  containerId: string;
  autosize?: boolean;
  timezone?: string;
  locale?: string;
  hideTopToolbar?: boolean;
  hideLegend?: boolean;
  hideVolume?: boolean;
  enablePublishing?: boolean;
  studies?: string[];
}

/**
 * Removes existing TradingView widget script if present
 */
export function removeExistingScript(): void {
  const existingScript = document.getElementById("tradingview-widget-script");
  if (existingScript && existingScript.parentNode) {
    existingScript.parentNode.removeChild(existingScript);
  }
}

/**
 * Creates and loads the TradingView widget script
 */
export function loadTradingViewScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (typeof window.TradingView !== "undefined") {
      resolve();
      return;
    }

    // Remove any existing script
    removeExistingScript();

    const script = document.createElement("script");
    script.id = "tradingview-widget-script";
    script.src = TRADINGVIEW_SCRIPT_URL;
    script.async = true;

    script.onload = () => {
      if (typeof window.TradingView !== "undefined") {
        resolve();
      } else {
        reject(
          new Error(
            "TradingView script loaded but TradingView object not found",
          ),
        );
      }
    };

    script.onerror = () => {
      reject(new Error("Failed to load TradingView script"));
    };

    document.head.appendChild(script);
  });
}

/**
 * Creates a TradingView widget with the given configuration
 */
export function createTradingViewWidget(config: TradingViewWidgetConfig): any {
  if (typeof window.TradingView === "undefined") {
    throw new Error(
      "TradingView is not loaded. Call loadTradingViewScript() first.",
    );
  }

  const widgetConfig = {
    autosize: config.autosize !== false,
    symbol: config.symbol,
    interval: config.interval,
    timezone: config.timezone || "Etc/UTC",
    theme: "dark",
    style: "1",
    locale: config.locale || "en",
    toolbar_bg: "#1a1a1a",
    enable_publishing: config.enablePublishing || false,
    hide_top_toolbar: config.hideTopToolbar || false,
    hide_legend: config.hideLegend || false,
    save_image: false,
    container_id: config.containerId,
    hide_volume: config.hideVolume || false,
    studies: config.studies || [
      "RSI@tv-basicstudies",
      "MASimple@tv-basicstudies",
    ],
  };

  return new window.TradingView.widget(widgetConfig);
}

/**
 * Generates a unique container ID
 */
export function generateContainerId(): string {
  return `tradingview-widget-container-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
